"use client";

import { useState } from "react";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { KeyRound, Loader2 } from "lucide-react";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "enroll">("login");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // First check if user has authenticators by trying to begin authentication
      const authRes = await fetch("/api/webauthn/authenticate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: "begin", email }),
      });

      if (!authRes.ok) {
        const data = await authRes.json();
        setError(data.error || "User not found");
        setLoading(false);
        return;
      }

      const { options, storeKey } = await authRes.json();

      if (options.allowCredentials && options.allowCredentials.length > 0) {
        // User has passkeys — authenticate
        const credential = await startAuthentication(options);
        const verifyRes = await fetch("/api/webauthn/authenticate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ step: "complete", storeKey, credential }),
        });

        if (!verifyRes.ok) {
          const data = await verifyRes.json();
          setError(data.error || "Authentication failed");
          setLoading(false);
          return;
        }

        window.location.href = "/";
      } else {
        // No passkeys — switch to enrollment mode
        setMode("enroll");
        setLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  }

  async function handleEnroll() {
    setError("");
    setLoading(true);

    try {
      const beginRes = await fetch("/api/webauthn/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: "begin", email }),
      });

      if (!beginRes.ok) {
        const data = await beginRes.json();
        setError(data.error || "Registration failed");
        setLoading(false);
        return;
      }

      const { options, userId } = await beginRes.json();
      const credential = await startRegistration(options);

      const completeRes = await fetch("/api/webauthn/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: "complete", userId, credential }),
      });

      if (!completeRes.ok) {
        const data = await completeRes.json();
        setError(data.error || "Registration failed");
        setLoading(false);
        return;
      }

      window.location.href = "/";
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-slate-800 rounded-xl flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-white">HC</span>
          </div>
          <h1 data-testid="signin-title" className="sr-only">Sign in</h1>
          <CardTitle className="text-xl text-white">Halocare Dev System</CardTitle>
          <CardDescription className="text-slate-400">
            {mode === "login"
              ? "Sign in with your passkey"
              : "Register a passkey for your account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {mode === "login" ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !email}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                Sign in with Passkey
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                No passkey found for <strong className="text-white">{email}</strong>.
                Register one now to access the console.
              </p>
              <Button
                onClick={handleEnroll}
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                Register Passkey
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-400"
                onClick={() => setMode("login")}
              >
                Back to sign in
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
